import PropTypes from 'prop-types'
import { Tab, Tabs } from 'baseui/tabs-motion'
import ContactTab from './Tabs/Contact'
import useSubscriptionForm from './useSubscriptionForm'
import MeterIds from './Tabs/MeterIds'
import { Delete } from 'baseui/icon'
import { useSelector } from 'react-redux'
import useTabBar from './useTabBar'
import useTabBarStyle from './useTabBarStyle'
import SaleForm from '../Layouts/SaleForm'
import { ErrorBoundary } from 'react-error-boundary'
import { Suspense } from 'react'
import Addresses from './Tabs/Addresses'
import CreateContractModal from '@/containers/SubscriptionFormPageNew/Components/CreateContractModal/CreateContractModal'
import { Button } from 'baseui/button'
import useCreateContractModal from '../Components/CreateContractModal/useCreateContractModal'
import AdditionalSalesContainer from '../Components/AdditionalSales/AdditionalSalesContainer'
import ClientTopHeader from '../Layouts/ClientTopHeader'
import { selectClientTabHasUnsavedChanges } from './SubscriptionFormSelectors'
import CopyContractLink from './CopyContractLink'
import UnsavedChangesBadge from './UnsavedChangesBadge/UnsavedChangesBadge'
import RightPanel from '@/containers/SubscriptionFormPageNew/Components/shared/RightPanel/RightPanel'
import RightPanelProvider from '@/containers/SubscriptionFormPageNew/Components/shared/RightPanel/RightPanelProvider'
import LegacyRightPanel from '@/containers/SubscriptionFormPageNew/Components/LegacyRightPanel/LegacyRightPanel'

const SubscriptionForm = ({ country, id }) => {
  const { activeTab, openedContracts, existingContracts, removeFromOpenedContracts, updateActiveTabHandler, t } =
    useTabBar()
  const clientTabHasUnsavedChanges = useSelector(selectClientTabHasUnsavedChanges)

  // 50%-alpha diagonal orange/yellow stripes used to flag unsaved changes
  // on the Client tab. The 50% alpha lets the underlying tab color tint
  // through (white when active, lightgrey when inactive).
  const dirtyStripesBackground =
    'repeating-linear-gradient(45deg, rgba(255, 232, 168, 0.5) 0, rgba(255, 232, 168, 0.5) 8px, rgba(255, 179, 71, 0.5) 8px, rgba(255, 179, 71, 0.5) 16px)'

  const { tabStyle, tabsOverrides, mainContainerStyle, tabsContainerStyle, addButtonStyle } = useTabBarStyle()

  const { toggleContractModal } = useCreateContractModal()

  useSubscriptionForm(id)

  return (
    <RightPanelProvider>
      <div style={mainContainerStyle}>
        <CreateContractModal />
        <div style={tabsContainerStyle}>
          <Tabs
            activeKey={activeTab}
            onChange={({ activeKey }) => updateActiveTabHandler(activeKey)}
            overrides={tabsOverrides}
            endEnhancer={
              <Button
                onClick={event => {
                  event.preventDefault()
                  toggleContractModal()
                }}
                overrides={addButtonStyle}
              >
                {t('subscriptionForm:contract_modal.tab_title')}
              </Button>
            }
          >
            <Tab
              title={
                <span
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    // Right-side breathing room for the absolutely-positioned
                    // badge so the next tab's button doesn't overlap it.
                    paddingRight: clientTabHasUnsavedChanges ? '24px' : 0,
                  }}
                >
                  {t('common:client')}
                  <UnsavedChangesBadge />
                </span>
              }
              key='client'
              overrides={{
                ...tabStyle,
                Tab: {
                  style: ({ $isActive }) => ({
                    ...tabStyle.Tab.style({ $isActive }),
                    ...(clientTabHasUnsavedChanges && {
                      backgroundImage: dirtyStripesBackground,
                      backgroundColor: $isActive ? 'white' : 'lightgrey',
                      ':hover': {
                        backgroundImage: dirtyStripesBackground,
                        backgroundColor: $isActive ? 'white' : 'lightgrey',
                      },
                      ':focus': {
                        backgroundImage: dirtyStripesBackground,
                        backgroundColor: $isActive ? 'white' : 'lightgrey',
                      },
                    }),
                  }),
                },
              }}
            >
              {/* Column wrapper: save bar on top, modules row below. */}
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <ClientTopHeader />
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: '48px',
                  }}
                >
                  <ContactTab />
                  <Addresses />
                  <MeterIds />
                </div>
              </div>
            </Tab>
            {openedContracts.length > 0 &&
              openedContracts.map(contractId => {
                const contract = existingContracts[contractId]
                if (!contract) {
                  return null
                }

                const contractKey = contract.id + '-' + contract.type
                return (
                  <Tab
                    key={contractKey}
                    artwork={() => (
                      <Delete
                        onClick={e => {
                          e.stopPropagation()
                          removeFromOpenedContracts({ id: contract.id, type: contract.type })
                        }}
                      />
                    )}
                    title={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        {`${t(`common:${contract.type}`)} ${contract.id}`}
                        {/* Quick-access deep link to this contract (URL + #id), copied to clipboard */}
                        <CopyContractLink
                          value={`${window.location.origin}${window.location.pathname}${window.location.search}#${contract.id}`}
                        />
                      </span>
                    }
                    overrides={tabStyle}
                  >
                    <div style={{ width: '100%', maxWidth: '100%', display: 'flex', flexGrow: 1 }}>
                      <ErrorBoundary fallback={<p>{t('common:error_contact_support')}</p>}>
                        <Suspense fallback={<div>{t('common:loading')}</div>}>
                          <SaleForm country={country} type={contract.type} />
                        </Suspense>
                      </ErrorBoundary>
                    </div>
                  </Tab>
                )
              })}
            <Tab title={t('additionalSales:common.tab_title')} key='additional-sales' overrides={tabStyle}>
              <AdditionalSalesContainer />
            </Tab>
          </Tabs>
        </div>
        <RightPanel country={country} />
        <LegacyRightPanel country={country} />
      </div>
    </RightPanelProvider>
  )
}

SubscriptionForm.propTypes = {
  id: PropTypes.string,
  country: PropTypes.string,
}

export default SubscriptionForm
